import { Activity, ChevronDown, CircleCheck, CircleX, Clock, ExternalLink, LoaderCircle, TriangleAlert, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { DayLevel, OverallStatus, ServiceStatus, StatusDay, StatusIncident, StatusLatencyPoint, StatusPage as StatusData, StatusServiceInfo } from '@spawnwire/shared';
import { clsx, Tooltip } from '../../components/ui';
import { apiUrl } from '../../lib/platform';

const REFRESH_MS = 30_000;

type Shown = ServiceStatus | 'unknown';

const SERVICE: Record<Shown, { label: string; dot: string; text: string; soft: string }> = {
  operational: { label: 'Operational', dot: 'bg-success', text: 'text-success', soft: 'bg-success/15' },
  degraded: { label: 'Degraded Performance', dot: 'bg-warning', text: 'text-warning', soft: 'bg-warning/15' },
  down: { label: 'Major Outage', dot: 'bg-danger', text: 'text-danger', soft: 'bg-danger/15' },
  unknown: { label: 'Checking…', dot: 'bg-offline', text: 'text-faint', soft: 'bg-offline/15' },
};

const BANNER: Record<OverallStatus, { icon: LucideIcon; text: string; box: string }> = {
  operational: { icon: CircleCheck, text: 'text-success', box: 'border-success/40 bg-success/10' },
  partial: { icon: TriangleAlert, text: 'text-warning', box: 'border-warning/40 bg-warning/10' },
  major: { icon: CircleX, text: 'text-danger', box: 'border-danger/40 bg-danger/10' },
  unknown: { icon: LoaderCircle, text: 'text-faint', box: 'border-line-strong bg-active' },
};

const DAY_BAR: Record<DayLevel, string> = { good: 'bg-success', warn: 'bg-warning', bad: 'bg-danger', none: 'bg-active' };

// ---- Formatting ----------------------------------------------------------------------------------------------------

function ago(iso: string | null, now: number): string {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86_400)} d ago`;
}

function duration(seconds: number): string {
  const m = Math.max(1, Math.round(seconds / 60));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
}

const pct = (v: number | null) => (v == null ? '—' : `${v === 100 ? '100' : v.toFixed(2)}%`);
const clock = (iso: string) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// ---- Data ----------------------------------------------------------------------------------------------------------

function useStatus() {
  const [data, setData] = useState<StatusData | null>(null);
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let alive = true;
    let controller: AbortController | null = null;
    const load = () => {
      controller?.abort();
      controller = new AbortController();
      fetch(apiUrl('/api/status'), { signal: controller.signal, headers: { accept: 'application/json' } })
        .then((res) => (res.ok ? (res.json() as Promise<StatusData>) : Promise.reject(new Error('bad status'))))
        .then((page) => {
          if (!alive) return;
          setData(page);
          setFailed(false);
        })
        .catch((err: unknown) => {
          if (alive && (err as { name?: string }).name !== 'AbortError') setFailed(true);
        });
    };
    load();
    const refresh = setInterval(load, REFRESH_MS);
    const tick = setInterval(() => setNow(Date.now()), 5000);
    return () => {
      alive = false;
      controller?.abort();
      clearInterval(refresh);
      clearInterval(tick);
    };
  }, []);

  return { data, failed, now };
}

// ---- Pieces --------------------------------------------------------------------------------------------------------

function UptimeBars({ days }: { days: StatusDay[] }) {
  return (
    <div className="flex h-9 items-stretch gap-[3px]" role="img" aria-label="Uptime for each of the last 30 days">
      {days.map((d) => (
        <Tooltip
          key={d.date}
          label={
            <span className="block text-center">
              <span className="block font-semibold">{new Date(`${d.date}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
              <span className="block text-muted">{d.uptime == null ? 'No data' : `${pct(d.uptime)} uptime`}</span>
            </span>
          }
        >
          <span className={clsx('min-w-0 flex-1 rounded-[3px] transition-opacity hover:opacity-70', DAY_BAR[d.level])} />
        </Tooltip>
      ))}
    </div>
  );
}

function LatencyChart({ points, color }: { points: StatusLatencyPoint[]; color: string }) {
  const valid = points.filter((p) => p.ms != null);
  if (valid.length < 2) {
    return <div className="flex h-14 items-center justify-center rounded-md bg-main/40 text-xs text-faint">Collecting response time data…</div>;
  }
  const W = 400;
  const H = 56;
  const t0 = new Date(points[0]!.t).getTime();
  const span = Math.max(1, new Date(points[points.length - 1]!.t).getTime() - t0);
  const max = Math.max(...valid.map((p) => p.ms!), 50);
  const x = (p: StatusLatencyPoint) => ((new Date(p.t).getTime() - t0) / span) * W;
  const y = (p: StatusLatencyPoint) => H - 4 - (p.ms! / max) * (H - 10);
  // Break the line where a stretch had no successful checks, instead of drawing across the gap.
  const segments: StatusLatencyPoint[][] = [];
  for (const p of points) {
    if (p.ms == null) segments.push([]);
    else (segments.at(-1) ?? segments[segments.push([]) - 1]!).push(p);
  }
  const lines = segments.filter((s) => s.length > 0);
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-14 w-full overflow-visible" role="img" aria-label="Response time over the last 24 hours">
        {lines.map((seg, i) => {
          const d = seg.map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p).toFixed(1)} ${y(p).toFixed(1)}`).join(' ');
          return (
            <g key={i}>
              {seg.length > 1 && <path d={`${d} L${x(seg.at(-1)!).toFixed(1)} ${H} L${x(seg[0]!).toFixed(1)} ${H} Z`} fill={color} opacity="0.12" />}
              <path d={d} fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              {seg.length === 1 && <circle cx={x(seg[0]!)} cy={y(seg[0]!)} r="2" fill={color} />}
            </g>
          );
        })}
      </svg>
      <div className="mt-1 flex justify-between text-[11px] text-faint">
        <span>24 h ago</span>
        <span>peak {Math.round(max)} ms</span>
        <span>now</span>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-faint">{label}</div>
      <div className="mt-0.5 text-base font-semibold tabular-nums text-fg">{value}</div>
    </div>
  );
}

function ServiceCard({ service, now }: { service: StatusServiceInfo; now: number }) {
  const meta = SERVICE[service.status];
  const [open, setOpen] = useState(false);
  const chartColor = service.status === 'down' ? 'var(--p-danger)' : service.status === 'degraded' ? 'var(--p-warning)' : 'var(--p-accent)';
  return (
    <section className="rounded-xl border border-line bg-sidebar p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-base font-semibold text-fg">
            <span className={clsx('relative flex size-2.5 shrink-0')}>
              {service.status === 'operational' && <span className={clsx('absolute inline-flex size-full animate-ping rounded-full opacity-40', meta.dot)} />}
              <span className={clsx('relative inline-flex size-2.5 rounded-full', meta.dot)} />
            </span>
            <span className="truncate">{service.name}</span>
          </h3>
          <p className="mt-0.5 pl-[18px] text-sm text-faint">{service.description}</p>
        </div>
        <div className="flex items-center gap-3 pl-[18px] sm:pl-0">
          {service.responseMs != null && (
            <span className="flex items-center gap-1 text-sm tabular-nums text-muted" title="Response time of the latest check">
              <Activity className="size-3.5 text-faint" aria-hidden />
              {service.responseMs} ms
            </span>
          )}
          <span className={clsx('rounded-full px-2.5 py-1 text-xs font-semibold', meta.soft, meta.text)}>{meta.label}</span>
        </div>
      </div>

      <div className="mt-4">
        <UptimeBars days={service.days} />
        <div className="mt-1.5 flex justify-between text-[11px] text-faint">
          <span>30 days ago</span>
          <span>Today</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 border-t border-line pt-4">
        <Stat label="24 hours" value={pct(service.uptime.h24)} />
        <Stat label="7 days" value={pct(service.uptime.d7)} />
        <Stat label="30 days" value={pct(service.uptime.d30)} />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-faint">
        <span className="flex items-center gap-1.5">
          <Clock className="size-3.5" aria-hidden />
          Last successful check: {service.lastSuccessAt ? ago(service.lastSuccessAt, now) : 'none yet'}
        </span>
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1 rounded px-1 py-0.5 text-muted hover:bg-hover hover:text-fg" aria-expanded={open}>
          Response time
          <ChevronDown className={clsx('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
      </div>
      {open && (
        <div className="mt-3 animate-fade">
          <LatencyChart points={service.latency} color={chartColor} />
        </div>
      )}
    </section>
  );
}

function IncidentCard({ incident, defaultOpen }: { incident: StatusIncident; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const ongoing = !incident.resolvedAt;
  const tone = incident.severity === 'down' ? SERVICE.down : SERVICE.degraded;
  return (
    <article className="rounded-xl border border-line bg-sidebar">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full items-start gap-3 rounded-xl p-4 text-left hover:bg-hover">
        <span className={clsx('mt-1.5 size-2.5 shrink-0 rounded-full', ongoing ? tone.dot : 'bg-success')} />
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-fg">{incident.title}</span>
          <span className="mt-0.5 block text-xs text-faint">
            {clock(incident.startedAt)}
            {incident.durationSeconds != null ? ` · resolved after ${duration(incident.durationSeconds)}` : ' · ongoing'}
          </span>
        </span>
        <span className={clsx('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold', ongoing ? [tone.soft, tone.text] : 'bg-success/15 text-success')}>
          {ongoing ? (incident.severity === 'down' ? 'Outage' : 'Degraded') : 'Resolved'}
        </span>
        <ChevronDown className={clsx('mt-1 size-4 shrink-0 text-faint transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {open && (
        <ol className="mx-4 mb-4 space-y-3 border-l border-line-strong pl-4">
          {incident.updates.map((u, i) => (
            <li key={i} className="relative">
              <span className="absolute -left-[21px] top-1.5 size-2 rounded-full bg-line-strong ring-2 ring-sidebar" />
              <div className="text-sm text-fg">
                <span className="font-semibold capitalize">{u.status}</span> <span className="text-muted">— {u.message}</span>
              </div>
              <div className="mt-0.5 text-xs text-faint">{clock(u.createdAt)}</div>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}

// ---- Page ----------------------------------------------------------------------------------------------------------

export function StatusPage() {
  const { data, failed, now } = useStatus();

  useEffect(() => {
    const previous = document.title;
    document.title = 'Spawnwire Status';
    return () => {
      document.title = previous;
    };
  }, []);

  const banner = data ? BANNER[data.overall.status] : BANNER.unknown;
  const BannerIcon = banner.icon;
  const intervalLabel = useMemo(() => (data ? `${data.checkIntervalSeconds}s` : ''), [data]);

  return (
    <div className="h-full overflow-y-auto bg-rail text-fg scroll-thin">
      <header className="border-b border-line bg-sidebar/60">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/icon-192.png" alt="Spawnwire" width={36} height={36} className="size-9 rounded-[10px]" />
            <div className="leading-tight">
              <div className="text-lg font-bold tracking-tight">spawnwire</div>
              <div className="text-xs font-medium text-faint">System status</div>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-accent-fg hover:bg-accent-hover">
            Open Spawnwire
            <ExternalLink className="size-3.5" aria-hidden />
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-8 px-4 py-6 sm:px-6 sm:py-8">
        {!data && !failed && (
          <div className="flex items-center justify-center gap-2 py-24 text-muted">
            <LoaderCircle className="size-5 animate-spin" aria-hidden /> Loading status…
          </div>
        )}
        {!data && failed && (
          <div className="rounded-xl border border-danger/40 bg-danger/10 p-5 text-center">
            <div className="font-semibold text-danger">Status is unavailable right now</div>
            <p className="mt-1 text-sm text-muted">We could not reach the status service. This page will keep trying.</p>
          </div>
        )}

        {data && (
          <>
            <div className={clsx('flex items-center gap-4 rounded-xl border p-5 sm:p-6', banner.box)} role="status" aria-live="polite">
              <BannerIcon className={clsx('size-9 shrink-0 sm:size-10', banner.text, data.overall.status === 'unknown' && 'animate-spin')} aria-hidden />
              <div className="min-w-0">
                <h1 className={clsx('text-xl font-bold sm:text-2xl', banner.text)}>{data.overall.label}</h1>
                <p className="mt-0.5 text-sm text-muted">{data.overall.message}</p>
              </div>
            </div>

            <div className="-mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-1 text-xs text-faint">
              <span>Last checked {ago(data.lastCheckedAt, now)}</span>
              <span>
                Updated {ago(data.generatedAt, now)} · checks run every {intervalLabel}
              </span>
            </div>

            {failed && <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-2.5 text-sm text-warning">Could not refresh just now. Showing the last data we received.</div>}

            {data.activeIncidents.length > 0 && (
              <section aria-labelledby="active-incidents" className="space-y-3">
                <h2 id="active-incidents" className="text-sm font-semibold uppercase tracking-wide text-faint">
                  Ongoing incidents
                </h2>
                {data.activeIncidents.map((i) => (
                  <IncidentCard key={i.id} incident={i} defaultOpen />
                ))}
              </section>
            )}

            <section aria-labelledby="services" className="space-y-3">
              <h2 id="services" className="text-sm font-semibold uppercase tracking-wide text-faint">
                Services
              </h2>
              {data.services.map((s) => (
                <ServiceCard key={s.id} service={s} now={now} />
              ))}
            </section>

            <section aria-labelledby="history" className="space-y-3">
              <h2 id="history" className="text-sm font-semibold uppercase tracking-wide text-faint">
                Incident history · last 30 days
              </h2>
              {data.recentIncidents.length === 0 ? (
                <div className="rounded-xl border border-line bg-sidebar p-5 text-center text-sm text-muted">No incidents in the last 30 days.</div>
              ) : (
                data.recentIncidents.map((i) => <IncidentCard key={i.id} incident={i} defaultOpen={false} />)
              )}
            </section>
          </>
        )}

        <footer className="pb-6 pt-2 text-center text-xs text-faint">Spawnwire · Times are shown in your local time zone</footer>
      </main>
    </div>
  );
}
